import type { Theme } from '@mui/material';
import { Paper, Typography, useTheme } from '@mui/material';
import type { DefaultizedPieSeriesType } from '@mui/x-charts/models';
import { PieChart } from '@mui/x-charts/PieChart';
import { isNotNullOrUndefined, isNullOrUndefined, oz, percentage, raiseUnreachable } from '@weft/utils';
import { formatPercentage } from 'apps/app/src/utils/number';
import { useState } from 'react';

const CHART_DATA_IDS = {
  PAID_PRICE_ID: 'data-id-paidPrice',
  OPEN_PRICE_ID: 'data-id-openPrice',
  PENDING_PRICE_ID: 'data-id-pendingPrice',
  OVERDUE_PRICE_ID: 'data-id-overduePrice',
} satisfies Record<string, `data-id-${keyof HomePageInvoiceStatusChartProps}`>;

const ChartDataIdsSchema = oz.nativeEnum(CHART_DATA_IDS);

type ChartDataIdsType = oz.infer<typeof ChartDataIdsSchema>;

type HomePageInvoiceStatusChartProps = {
  paidPrice: number;
  openPrice: number;
  pendingPrice: number;
  overduePrice: number;
};

export const HomePageInvoiceStatusChart: React.FC<HomePageInvoiceStatusChartProps> = ({ openPrice, overduePrice, paidPrice, pendingPrice }) => {
  const vyTheme = useTheme();
  const [lastHoveredId, setLastHoveredId] = useState<ChartDataIdsType | null>(null);
  const totalValue = openPrice + overduePrice + paidPrice + pendingPrice;

  return (
    <PieChart    
      width={230}
      height={230}
      slotProps={{ legend: { hidden: true } }}
      tooltip={{
        trigger: 'item',
        itemContent: (props) => {
          const { itemData, series } = props;
          const pieSeries = series as DefaultizedPieSeriesType;

          if (isNullOrUndefined(itemData.dataIndex)) {
            return null;
          }

          const hoveredItemData = pieSeries.data[itemData.dataIndex];
          const idAsString = String(hoveredItemData.id);

          const parsedId = ChartDataIdsSchema.safeParse(idAsString);

          if (!parsedId.success) {
            return null;
          }
            
          setLastHoveredId(parsedId.data);
          const valueAmountRatio = percentage(hoveredItemData.value, totalValue);

          return (
            <Paper sx={{
              backgroundColor: '#0D1B27DE',
              padding: 1,
              transform: 'translate(-50%, -120%)',
            }}
            >
              <Typography
                fontSize="14px"
                lineHeight="18px"
                fontWeight={500}
                textAlign="center"
                color="white"
              >
                {`${getChartDataHighlightLabel(parsedId.data)}`}
                <br />
                {formatPercentage(valueAmountRatio)}
              </Typography>
            </Paper>
          );
        },
      }}
      series={[
        {
          highlightScope: isNotNullOrUndefined(lastHoveredId) ? { faded: 'none', highlighted: 'item' } : undefined,
          highlighted: isNotNullOrUndefined(lastHoveredId) ? { color: getChartDataBaseColor(lastHoveredId, vyTheme).dark } : undefined,
          data: [{
            id: CHART_DATA_IDS.PAID_PRICE_ID,
            value: paidPrice,
            label: 'Paid',
            color: getChartDataBaseColor(CHART_DATA_IDS.PAID_PRICE_ID, vyTheme).light,
          },
          {
            id: CHART_DATA_IDS.OPEN_PRICE_ID,
            value: openPrice,
            label: 'Open',
            color: getChartDataBaseColor(CHART_DATA_IDS.OPEN_PRICE_ID, vyTheme).light,
          },
          {
            id: CHART_DATA_IDS.PENDING_PRICE_ID,
            value: pendingPrice,
            label: 'Pending Payment',
            color: getChartDataBaseColor(CHART_DATA_IDS.PENDING_PRICE_ID, vyTheme).light,
          },
          {
            id: CHART_DATA_IDS.OVERDUE_PRICE_ID,
            value: overduePrice,
            label: 'Overdue',
            color: getChartDataBaseColor(CHART_DATA_IDS.OVERDUE_PRICE_ID, vyTheme).light,
          }],
          innerRadius: 60,
          outerRadius: 100,
          paddingAngle: 0.5,
          cornerRadius: 0,
          startAngle: 0,
          endAngle: 360,
          cx: 115,
          cy: 115,
        },
      ]}
    />
  );
};

function getChartDataHighlightLabel(id: ChartDataIdsType) {
  switch (id) {
    case CHART_DATA_IDS.PAID_PRICE_ID:
      return 'Paid Invoices';
    case CHART_DATA_IDS.OPEN_PRICE_ID:
      return 'Open Invoices';
    case CHART_DATA_IDS.PENDING_PRICE_ID:
      return 'Pending Payment Invoices';
    case CHART_DATA_IDS.OVERDUE_PRICE_ID:
      return 'Overdue Invoices';
    default:
      return raiseUnreachable(id);
  }
}

function getChartDataBaseColor(id: ChartDataIdsType, theme: Theme) {
  switch (id) {
    case CHART_DATA_IDS.PAID_PRICE_ID:
      return theme.palette.success;
    case CHART_DATA_IDS.OPEN_PRICE_ID:
      return theme.palette.primary;
    case CHART_DATA_IDS.PENDING_PRICE_ID:
      return theme.palette.warning;
    case CHART_DATA_IDS.OVERDUE_PRICE_ID:
      return theme.palette.error;
    default:
      return raiseUnreachable(id);
  }
}